import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { first, map, Observable } from 'rxjs';

import { StateService } from '../infrastructure/state-service.service';
import { CheckoutState } from './states/checkout.state';

/**
 * Keeps the cinema checkout's later steps out of reach without a cart: every step past the entry
 * ones is only meaningful once a reservation exists, and `cart_id` is written into the URL by the
 * first one and merged into every navigation after it.
 *
 * The cart's *status* is deliberately not checked here. This guard also runs on a cold deep link,
 * before CheckoutWrapperComponent has fetched anything, so CartState would still be empty and every
 * arrival would be turned away as expired. The cart-expired interceptor handles that case instead.
 */
@Injectable({
  providedIn: 'root',
})
export class StepGuard {
  constructor(
    private readonly checkoutState: CheckoutState,
    private router: Router,
    private stateService: StateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.stateService.reinitializing$.pipe(
      first(),
      map(() => {
        const cartId = route.queryParamMap.get('cart_id');
        if (!cartId) {
          return this.startUrl(state.url);
        }
        return true;
      })
    );
  }

  private startUrl(url: string): boolean | UrlTree {
    const { showId, posId } = this.extractIds(url);
    if (!showId || !posId) {
      return false;
    }
    const defaultStepRoute = this.checkoutState.firstStep?.route ?? 'tickets';
    return this.router.createUrlTree([posId, 'checkout', showId, defaultStepRoute]);
  }

  private extractIds(url: string): { posId: string | null; showId: string | null } {
    const regex = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
    const match = url.match(regex);
    return {
      posId: match ? match[0] : null,
      showId: match ? match[1] : null,
    };
  }
}
